"use client"

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { buildTypeFAQs } from "@/lib/faq/legacy-spec-faq-templates"

interface ProductFAQProps {
  product: any
  analysis: any
}

/**
 * LEGACY product FAQ component — retired 2026-07-10, kept for recovery.
 *
 * Renders the frozen v1 templates from lib/faq/legacy-spec-faq-templates.ts.
 * Only mounted when NEXT_PUBLIC_MODULE_SPEC_FAQ_LEGACY='1' (recovery mode —
 * see docs/Page-Types-And-Templates.md §3.3/§9). The replacement is
 * components/blocks/product-faq/spec-faq.tsx fed by lib/faq/spec-faq-builder.ts.
 */
export function ProductFAQ({ product, analysis }: ProductFAQProps) {
  const faqs = buildTypeFAQs(product, analysis)
  const [openFaqs, setOpenFaqs] = useState<Set<number>>(new Set([0, 1]))

  // FAQ rewrite WS1: this component's own FAQPage JSON-LD is RETIRED
  // PERMANENTLY — one FAQPage per page, emitted server-side by
  // <ProductSchemas> from visible content only. Even in recovery mode
  // (NEXT_PUBLIC_MODULE_SPEC_FAQ_LEGACY='1') only the visible copy returns.

  if (faqs.length === 0) return null

  return (
    <>
      <section id="faq" className="mt-12 pt-10 border-t border-gray-200">
        <header className="mb-6">
          <p className="text-[#ff9951] font-bold tracking-widest text-xs uppercase mb-2">
            Before You Buy
          </p>
          <h2 className="text-2xl md:text-3xl font-black tracking-tighter text-gray-900 leading-tight">
            Frequently Asked Questions
          </h2>
        </header>
        <div className="space-y-4">
          {faqs.map((faq, i) => {
            const isOpen = openFaqs.has(i)
            return (
              <div
                key={i}
                className={`rounded-lg overflow-hidden transition-colors ${isOpen ? "bg-[#0469f0]/[0.05]" : "bg-[#0469f0]/[0.02]"}`}
              >
                <button
                  type="button"
                  onClick={() => {
                    const next = new Set(openFaqs)
                    if (next.has(i)) next.delete(i)
                    else next.add(i)
                    setOpenFaqs(next)
                  }}
                  aria-expanded={isOpen}
                  className="w-full p-5 flex items-center justify-between text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40"
                >
                  <span className="font-bold text-gray-900 text-sm pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">
                    <span dangerouslySetInnerHTML={{ __html: faq.answer.replace(/Fridge\.com/g, '<strong>Fridge.com</strong>') }} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>
    </>
  )
}
